import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    FavoritePreference,
    FavoritesFacade,
    PurchaseService,
    ShoppingCartService
} from '@ridango/components';
import { NextButtonElement, Preferences, Medium } from '@ridango/components/lib/purchase/purchase.model';
import { AuthService, BaseComponent, TravelMedia } from '@ridango/core';
import { takeUntil } from 'rxjs/operators';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import Env from '@ridango/core/env';
import { EstoniaPersonalCodeValidator } from '@ridango/core/lib/validators/personal-code/estonia-personal-code.validator';
import { Router } from '@angular/router';

@Component({
    selector: 'gl-purchase-v2-ticket-preferences',
    templateUrl: './preferences.component.html',
    styleUrls: ['./preferences.component.scss']
})
export class PreferencesComponent extends BaseComponent implements OnInit, OnDestroy {
    @Output() nextStep: EventEmitter<void> = new EventEmitter<void>();
    @Output() nextButtonElement = new EventEmitter<NextButtonElement>();
    @Output() savePreference = new EventEmitter<FavoritePreference>();
    @Output() selectedOptionChange = new EventEmitter<Medium>();
    @Input() preferences: Preferences[];
    assetsPath$ = this.applicationFacade.assetsPath$;
    @Input() selectedPreferenceId: number;
    currentLanguage = this.translate.currentLang;
    options = this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.MEDIA_OPTIONS ?? [];
    selectedOption: TravelMedia;
    otherFareMediaForm: FormGroup;
    @Input() savedPreference: FavoritePreference;
    savedPreferenceCheckbox: boolean;
    selectedPreference: Preferences;
    TravelMedia = TravelMedia;
    travelCardPrefix: string;
    mediums: Medium[];
    userLoggedIn = false;

    readonly TRAVEL_CARD_WITH_PREFIX_LENGTH: number = 19;
    bankCardEnable = !!this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.BANK_CARD_ENABLE;
    combinedCodeCard = !!this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.COMBINED_PERSONAL_CODE_TRAVEL_CARD;
    constructor(
        @Inject('ENV') public env: Env,
        private router: Router,
        private applicationFacade: ApplicationFacade,
        private purchaseService: PurchaseService,
        private translate: TranslateService,
        private favoritesFacade: FavoritesFacade,
        private authService: AuthService,
        private shoppingCartService: ShoppingCartService
    ) {
        super();
    }
    ngOnInit(): void {
        this.userLoggedIn = this.authService.getLoginStatus();
        if (!this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.MEDIA_OPTIONS) {
            this.purchaseService
                .getMediums()
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(
                    (mediums) => {
                        let initialMediums = [...mediums];

                        if (this.combinedCodeCard) {
                            initialMediums = initialMediums.filter((m) => m.code !== 'PERSONAL_CODE');

                            if (this.bankCardEnable && !this.userLoggedIn) {
                                const bankCard = {
                                    id: '9999999',
                                    code: 'BANK_CARD',
                                    name: null,
                                    description: null,
                                    products: null,
                                    parameterizedDerivedProduct: null
                                };
                                const insertAfterFirstEle = initialMediums.length > 0 ? 1 : 0;
                                initialMediums.splice(insertAfterFirstEle, 0, bankCard);
                            }
                        }

                        this.mediums = initialMediums;
                        this.options = initialMediums.map((m) => m.code as TravelMedia);
                        this.selectedOption = this.options[0];
                        this.selectedOptionChange.emit(this.mediums[0]);
                        this.purchaseService.setPurchaseDataLoading(false);
                    },
                    (error) => {
                        console.error('Error fetching mediums:', error);
                        this.purchaseService.setPurchaseDataLoading(false);
                    }
                );
        } else {
            this.selectedOption = this.options[0];
        }

        this.purchaseService.setPurchaseDataLoading(false);
        if (this.savedPreference) {
            this.savedPreferenceCheckbox = true;
        }
        this.selectedOption = this.options[0];
        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.currentLanguage = event.lang;
        });
        this.otherFareMediaForm = new FormGroup({
            otherFareMedia: new FormControl('', [Validators.required])
        });

        this.travelCardPrefix = this.shoppingCartService.getTravelCardPrefix();
        this.setValidators();
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: true
        });
    }

    get isLogin() {
        return this.authService.getLoginStatus();
    }

    ngOnDestroy(): void {
        if (this.env.PURCHASE_FLOW?.SAVE_PREFERENCE_ENABLED && this.selectedPreference) {
            const preference = {
                preferenceType: this.selectedPreference.code,
                preferenceValue: this.selectedPreference.value
            };

            if (this.savedPreferenceCheckbox) {
                this.favoritesFacade.addFavoritePreference(preference);
            } else {
                this.favoritesFacade.deleteFavoritePreference();
            }
        }
        this.purchaseService.choosePreference(this.selectedPreference);
        this.nextButtonElement.emit(null);
        super.ngOnDestroy();
    }

    async selectPreference(preferenceId: number) {
        this.selectedPreference = this.preferences?.find((preference) => preference.id == preferenceId);
        const matchingMedium = this.mediums?.find((medium) => medium.code === this.selectedPreference?.code);
        if (matchingMedium) {
            this.selectedPreference.id = Number(matchingMedium.id);
            this.selectedOptionChange.emit({ ...matchingMedium, value: this.selectedPreference.value });
        }
        this.nextStep.emit();
    }

    public addOtherFareMedia(option: TravelMedia) {
        this.selectedOption = option;
        this.otherFareMediaForm.controls.otherFareMedia.reset();
        const selectedMedium = this.mediums?.find((medium) => medium.code === this.selectedOption);

        if (selectedMedium) {
            this.selectedOptionChange.emit(selectedMedium);
        }
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: true
        });
        this.setValidators();
    }

    public onInputChange(e: any) {
        const targetValue: string = e.target.value;
        const mediaFormCtrl = this.otherFareMediaForm.get('otherFareMedia');
        mediaFormCtrl.setValue(targetValue);
        mediaFormCtrl.updateValueAndValidity();
        this.setValidators();
        let code = this.selectedOption;
        if (this.selectedOption === TravelMedia.card && this.combinedCodeCard) {
            const isPersonalCodeValid =
                new EstoniaPersonalCodeValidator().getPersonalCodeValidator()(mediaFormCtrl) === null;
            code = isPersonalCodeValid ? TravelMedia.personalCode : TravelMedia.card;
        }

        const matchingMedium = this.mediums?.find((medium) => medium.code === code);
        this.selectedPreference = {
            code,
            value: targetValue,
            id: matchingMedium ? Number(matchingMedium.id) : undefined
        };
        this.nextButtonElement.emit({
            content: { textTerm: 'BUTTON_NEXT' },
            disabled: !mediaFormCtrl.valid
        });
    }

    public savePreferenceHandler() {
        this.savedPreferenceCheckbox = !this.savedPreferenceCheckbox;
    }

    private setValidators() {
        const mediaFormCtrl = this.otherFareMediaForm.get('otherFareMedia');
        mediaFormCtrl.clearValidators();
        mediaFormCtrl.addValidators(Validators.required);

        if (this.selectedOption === TravelMedia.email) {
            mediaFormCtrl.addValidators(Validators.email);
        } else if (this.selectedOption === TravelMedia.personalCode) {
            const personalCodeRegexp = new RegExp(
                this.env.PURCHASE_FLOW?.TICKET_CONF?.PREFERENCES?.PERSONAL_CODE_REGEXP
            );
            mediaFormCtrl.addValidators(Validators.pattern(personalCodeRegexp));
        } else if (this.selectedOption === TravelMedia.card) {
            const cardLength = this.env.PURCHASE_FLOW?.UTN_TRAVEL_CARD_LENGTH ?? 0;
            const matkakorttiLength = this.env.PURCHASE_FLOW?.MATKAKORTTI_LENGTH ?? 0;

            const travelCardRegexp = new RegExp(`^[0-9]{${cardLength}}$`);
            const matkakorttiRegexp = new RegExp(`^[0-9]{${matkakorttiLength}}$`);

            const cardValidator: ValidatorFn = (control: AbstractControl) => {
                const value = control.value;
                if (!value) return null;

                if (travelCardRegexp.test(value) || matkakorttiRegexp.test(value)) {
                    return null;
                }
                return { invalidCardLength: true };
            };

            if (this.combinedCodeCard) {
                const personalValidator = new EstoniaPersonalCodeValidator().getPersonalCodeValidator();
                const combined: ValidatorFn = (c: AbstractControl) => {
                    if (personalValidator(c) === null) return null;
                    return cardValidator(c);
                };
                mediaFormCtrl.addValidators(combined);
            } else {
                mediaFormCtrl.addValidators(cardValidator);
            }
        }

        mediaFormCtrl.updateValueAndValidity();
    }

    public hasSavedPreferences(): boolean {
        return this.preferences && this.preferences.length > 0;
    }

    navigateToLogin(): void {
        this.router.navigate([this.translate.currentLang, 'login']);
    }
}
